import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"

import {
  useConfirmReferralMutation,
  useCreateReferralMutation,
  useLazyGetArchivedReferralsQuery,
  useLazyGetReferralsQuery,
  useUpdateReferralMutation,
} from "store/api/referralsApi"
import { ApiTypes, UITypes } from "types"
import { TableActionButtonGroup } from "types/table/tableActionButton"

import { convertReferralsToTableRows } from "utils/convertData"
import { isMobileDevice } from "utils/deviceDetection"
import getErrorMessage from "utils/getErrorMessage"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import TableWrapper from "components/table/TableWrapper"

import { DenyReferralModal } from "./DenyReferralModal"
import { MyReferralsHeader } from "./MyReferralsHeader"
import { ReferralModal } from "./ReferralModal"
import { getReferralsTableHeaderCells } from "./referralsTableHeaderCells"

import localStyles from "./index.module.scss"

type ReferralModalState = { mode: "add" } | { mode: "edit" | "review"; referral: ApiTypes.Model.Referral }

export const MyReferrals = () => {
  const [showArchive, setShowArchive] = useState(false)
  const [modalState, setModalState] = useState<ReferralModalState | null>(null)
  const [denyReferralId, setDenyReferralId] = useState<number | null>(null)

  const [triggerGetReferrals, { isFetching: isFetchingActive, data: activeData }] = useLazyGetReferralsQuery()
  const [triggerGetArchived, { isFetching: isFetchingArchived, data: archivedData }] =
    useLazyGetArchivedReferralsQuery()
  const [triggerCreateReferral] = useCreateReferralMutation()
  const [triggerUpdateReferral] = useUpdateReferralMutation()
  const [triggerConfirmReferral] = useConfirmReferralMutation()

  const { urlQueryParams } = useUrlQueryParams()

  const data = showArchive ? archivedData : activeData
  const isFetching = showArchive ? isFetchingArchived : isFetchingActive

  const getReferrals = useCallback(async () => {
    try {
      const trigger = showArchive ? triggerGetArchived : triggerGetReferrals
      const params = { ...urlQueryParams }
      if (showArchive) delete params.status
      const res = await trigger(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }, [showArchive, urlQueryParams, triggerGetArchived, triggerGetReferrals])

  useEffect(() => {
    getReferrals()
  }, [getReferrals])

  const getRowActions = useCallback((rowData: UITypes.TableBodyRow): TableActionButtonGroup => {
    const referral = rowData.rowSettings.item as ApiTypes.Model.Referral
    const status = referral?.status

    return {
      className: localStyles.tableActionsWrapper,
      actionButtons: [
        {
          icon: "Deny",
          className: localStyles.dangerAction,
          hidden: status !== "pending_approval",
          handler: id => setDenyReferralId(id),
        },
        {
          icon: "Review & Confirm",
          hidden: status !== "pending_approval",
          handler: () => setModalState({ mode: "review", referral }),
        },
        {
          icon: "Edit",
          hidden: status !== "submitted",
          handler: () => setModalState({ mode: "edit", referral }),
        },
      ],
    }
  }, [])

  const handleModalSave = async (values: ApiTypes.Req.CreateReferral) => {
    if (!modalState) return

    try {
      if (modalState.mode === "add") {
        const res = await triggerCreateReferral(values)
        const error = getErrorMessage(res?.error)

        if (error) {
          toast.error(error)
          return
        }

        toast.success("Referral has been successfully submitted!")
      } else {
        const { referral, mode } = modalState
        const updateRes = await triggerUpdateReferral({ id: referral.id, body: values })
        const updateError = getErrorMessage(updateRes?.error)

        if (updateError) {
          toast.error(updateError)
          return
        }

        if (mode === "review") {
          const confirmRes = await triggerConfirmReferral(referral.id)
          const confirmError = getErrorMessage(confirmRes?.error)

          if (confirmError) {
            toast.error(confirmError)
            return
          }
        }

        toast.success(mode === "review" ? "Referral has been confirmed!" : "Referral has been successfully updated!")
      }

      setModalState(null)
    } catch (error) {
      console.error(error)
    }
  }

  const handleDenySuccess = () => setDenyReferralId(null)

  const tableRows = convertReferralsToTableRows(data?.data)

  return (
    <>
      <ContentWrapper title="Add a referral">
        <MyReferralsHeader
          tableRows={tableRows}
          onAddClick={() => setModalState({ mode: "add" })}
          onArchiveChange={setShowArchive}
        />
        <TableWrapper
          headerCells={getReferralsTableHeaderCells(showArchive)}
          data={tableRows}
          isLoading={isFetching}
          getRowActions={getRowActions}
          isMobileDevice={isMobileDevice()}
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
      {denyReferralId !== null && (
        <DenyReferralModal
          referralId={denyReferralId}
          onCancel={() => setDenyReferralId(null)}
          onSuccess={handleDenySuccess}
        />
      )}
      {modalState && (
        <ReferralModal
          mode={modalState.mode}
          referral={modalState.mode !== "add" ? modalState.referral : undefined}
          onCancel={() => setModalState(null)}
          onSave={handleModalSave}
        />
      )}
    </>
  )
}
